// @flow

import Honeybadger from '@honeybadger-io/js';
import store from 'store2';
import { v4 as uuidv4 } from 'uuid';

import { DEPLOYMENT_ENVIRONMENT } from 'shared/config';
import { localStorageAvailable } from 'shared/utils/localStorage';
import { logger } from 'shared/utils/logger';

import type { Context } from './types';

const apiKey = 'f65457ed';

const state = {
  initialised: false
};

const honeyBadgerThirdPartyExclusionList = [
  /front.optimonk.com/,
  /cdn-asset.optimonk.com/,
  /usabilla.com/,
  /hotjar.com/,
  /googletagmanager.com/,
  /googleads.g.doubleclick.net/,
  /content-uk.fundingcircle.com/, // Ignore errors from scripts hosted by Threatmetrix
  /chrome-extension:\/\//,
  /safari-extension:\/\//,
  /webkit-masked-url/
];

const honeyBadgerExceptionMessageExclusionList = [
  /ReferenceError: Can't find variable: ErrorReporter/,
  /TypeError: window.usabilla_live is not a function/,
  /Object doesn't support property or method 'usabilla_live'/,
  /'?UET'? is not defined/, // GTM trying to load ad-blocked dependencies
  /a\[b\].target.className.indexOf/, // Error from lottie-web: https://github.com/airbnb/lottie-web/issues/1134
  /TypeError: Cannot read property 'closingEls' of undefined/, // Chrome extension trying to append scripts
  /require is not defined/, // Require should only come from 3rd party scripts since we use import
  /UnhandledPromiseRejectionWarning: Object Not Found Matching Id:[1-9]$/, // This is an error that has been traced back to Marketing campaigns being sent out via a 3rd party service called Acoustic
  /UnhandledPromiseRejectionWarning: Error: CWR: ([a-zA-Z]+\s)+/, // Related to CloudWatch RUM, does not affect users - More info SRE-1337
  /UnhandledPromiseRejectionWarning: \{"code":4001,"message":"wallet must has at least one account"\}/, // Error originates from a Chrome extension
  /Error: XHR request errored/, // Comes from opentelemetry networking errors
  /window.__tcfapi is not a function/, // This error is thrown by the OneTrust cookie consent tool
  /amplitude is not defined/ // This error is thrown by the Amplitude analytics tool when OUI is visited by web crawlers or bots
];

const honeybadgerURLExclusionList = [
  /^file:/,
  /translate\.google/,
  /optimizelyedit\.com/
];

const inThirdPartyExclusionList = (notice) =>
  honeyBadgerThirdPartyExclusionList.some((pattern) =>
    pattern.test(notice.stack)
  );

const inExceptionMessageExclusionList = (notice) =>
  honeyBadgerExceptionMessageExclusionList.some((pattern) =>
    pattern.test(notice.message)
  );

const inURLExclusionList = (notice) =>
  honeybadgerURLExclusionList.some((pattern) => pattern.test(notice.url));

const isForeignBacktrace = (notice) =>
  typeof notice.stack === 'string' &&
  !notice.stack.includes('/uk/originations/dist');

export const shouldNotify = (notice: any) =>
  !(
    inThirdPartyExclusionList(notice) ||
    inExceptionMessageExclusionList(notice) ||
    inURLExclusionList(notice)
  );

export const annotateThirdPartyErrors = (notice: any) => {
  // https://docs.honeybadger.io/lib/javascript/guides/customizing-error-reports.html#modifying-data
  if (isForeignBacktrace(notice)) {
    notice.message = `[3rd Party error] ${notice.message}`;
    // HB removes any space in between words so it's better to keep it this way
    notice.context.tags = '3rdParty';
  }
};

// Add a unique ID for this user to help identify how many people are affected by an alert
// This is independent of the loan application ID
export const addUserSessionId = (notice: any) => {
  const getOrSet = (store, key) => {
    let v = store.get(key);

    if (!v) {
      v = uuidv4();
      store.set(key, v);
    }

    return v;
  };

  notice.context.user_id = localStorageAvailable()
    ? getOrSet(store, 'honeybadger-user-id')
    : uuidv4();
};

export const init = () => {
  state.initialised = false;
  if (DEPLOYMENT_ENVIRONMENT !== 'local') {
    Honeybadger.beforeNotify(shouldNotify);
    Honeybadger.beforeNotify(annotateThirdPartyErrors);
    Honeybadger.beforeNotify(addUserSessionId);

    Honeybadger.configure({
      apiKey,
      environment: DEPLOYMENT_ENVIRONMENT,
      revision: process.env.RELEASE,
      // https://docs.honeybadger.io/lib/javascript/guides/reducing-noise.html
      maxErrors: 5
    });

    state.initialised = true;
  }
};

export const captureException = (err: Error, errorInfo: any) => {
  if (state.initialised) {
    Honeybadger.notify(err, errorInfo);
  } else {
    logger.log('honeybadger not setup, would have tracked:', err, errorInfo);
  }
};

export const setContext = ({ applicationId }: Context) => {
  Honeybadger.setContext({
    applicationId
  });
};
